import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Avatar, LinearProgress,
  CircularProgress, Button, CardActionArea
} from '@mui/material';
import { Store, ShoppingCart, Warning, CheckCircle, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'https://api.bharatdarshan.app/api';

const MarketplaceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVendors: 0, activeVendors: 0, pendingKYC: 0,
    totalProducts: 0, pendingProducts: 0, liveProducts: 0,
    totalOrders: 0, pendingOrders: 0, completedOrders: 0,
    totalRevenue: 0, totalCategories: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [pendingKYCVendors, setPendingKYCVendors] = useState([]);

  const formatDate = (dateStr) => { if (!dateStr) return 'N/A'; return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const dashRes = await fetch(`${API_BASE}/marketplace/admin/dashboard`).then(r => r.json()).catch(() => null);
      if (dashRes && dashRes.success && dashRes.stats) {
        const s = dashRes.stats;
        setStats({
          totalVendors: (s.vendors && s.vendors.total) || 0,
          activeVendors: (s.vendors && s.vendors.active) || 0,
          pendingKYC: (s.vendors && s.vendors.pending) || 0,
          totalProducts: (s.products && s.products.total) || 0,
          liveProducts: (s.products && s.products.live) || 0,
          pendingProducts: (s.products && s.products.pending) || 0,
          totalOrders: (s.orders && s.orders.total) || 0,
          pendingOrders: (s.orders && s.orders.pending) || 0,
          completedOrders: (s.orders && s.orders.completed) || 0,
          totalRevenue: (s.revenue && s.revenue.total) || 0,
          totalCategories: s.categories || 0,
        });
        setRecentOrders(Array.isArray(dashRes.recentOrders) ? dashRes.recentOrders.slice(0, 8) : []);
      }

      const vendorsRes = await fetch(`${API_BASE}/vendor/admin/all`).then(r => r.json()).catch(() => null);
      if (vendorsRes && vendorsRes.success && Array.isArray(vendorsRes.data)) {
        const pending = vendorsRes.data
          .filter(v => ['pending', 'submitted'].includes((v.kycStatus || '').toLowerCase()))
          .slice(0, 5)
          .map(v => ({
            id: v._id,
            name: v.businessName || v.ownerName || 'Vendor',
            category: v.vendorType || 'general',
            appliedOn: formatDate(v.createdAt)
          }));
        setPendingKYCVendors(pending);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboardData(); }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED': return 'success';
      case 'OUT_FOR_DELIVERY': return 'info';
      case 'PACKED': return 'primary';
      case 'ACCEPTED': return 'info';
      case 'CANCELLED':
      case 'REJECTED': return 'error';
      default: return 'warning';
    }
  };

  if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><CircularProgress /></Box>);

  const statCards = [
    { label: 'Total Vendors', value: stats.totalVendors, color: '#4CAF50', icon: <Store sx={{ fontSize: 50, opacity: 0.7 }} />, path: '/marketplace/vendors' },
    { label: 'Active Vendors', value: stats.activeVendors, color: '#2196F3', icon: <CheckCircle sx={{ fontSize: 50, opacity: 0.7 }} />, path: '/marketplace/vendors' },
    { label: 'Pending KYC', value: stats.pendingKYC, color: '#FF9800', icon: <Warning sx={{ fontSize: 50, opacity: 0.7 }} />, path: '/marketplace/vendors' },
    { label: 'Pending Products', value: stats.pendingProducts, color: '#9C27B0', icon: <ShoppingCart sx={{ fontSize: 50, opacity: 0.7 }} />, path: '/marketplace/products' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>Marketplace Dashboard</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchDashboardData}>Refresh</Button>
      </Box>
      <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>Vendor Overview</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ bgcolor: stat.color, color: 'white', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }} onClick={() => navigate(stat.path)}>
              <CardActionArea>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box><Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography><Typography>{stat.label}</Typography></Box>
                    {stat.icon}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>Orders & Revenue</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => navigate('/marketplace/orders')}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Orders</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>{stats.totalOrders}</Typography>
              <Typography variant="body2" color="textSecondary">{stats.pendingOrders} pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Completed Orders</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>{stats.completedOrders}</Typography>
              <Typography variant="body2" color="textSecondary">Delivered to customers</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Revenue</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196F3' }}>{formatCurrency(stats.totalRevenue)}</Typography>
              <Typography variant="body2" color="textSecondary">From delivered orders</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Categories</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9C27B0' }}>{stats.totalCategories}</Typography>
              <Typography variant="body2" color="textSecondary">Active categories</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Recent Orders</Typography>
            {recentOrders.length === 0 ? (<Typography color="textSecondary" align="center" sx={{ py: 3 }}>No recent orders available</Typography>) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Invoice</b></TableCell>
                      <TableCell><b>Amount</b></TableCell>
                      <TableCell><b>Status</b></TableCell>
                      <TableCell><b>Date</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOrders.map((order) => (
                      <TableRow key={order._id} hover>
                        <TableCell>{order.invoiceId || (order._id || '').slice(-8)}</TableCell>
                        <TableCell>{formatCurrency(order.totalPaid || order.amount || 0)}</TableCell>
                        <TableCell><Chip label={order.status} color={getStatusColor(order.status)} size="small" /></TableCell>
                        <TableCell>{formatDate(order.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Pending KYC Approvals</Typography>
            {pendingKYCVendors.length === 0 ? (<Typography color="textSecondary" align="center" sx={{ py: 3 }}>No pending KYC approvals</Typography>) : (
              pendingKYCVendors.map((vendor) => (
                <Box key={vendor.id} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#e8e8e8' } }} onClick={() => navigate('/marketplace/vendors')}>
                  <Avatar sx={{ bgcolor: '#4CAF50', mr: 2 }}>{vendor.name[0]}</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight="bold">{vendor.name}</Typography>
                    <Typography variant="caption" color="textSecondary">{vendor.category} &bull; {vendor.appliedOn}</Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Products Overview</Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Live Products</Typography>
                <Typography variant="body2">{stats.liveProducts} / {stats.totalProducts || 1}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={stats.totalProducts ? (stats.liveProducts / stats.totalProducts) * 100 : 0} sx={{ height: 8, borderRadius: 4 }} color="success" />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Pending Approval</Typography>
                <Typography variant="body2">{stats.pendingProducts}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={stats.totalProducts ? (stats.pendingProducts / stats.totalProducts) * 100 : 0} sx={{ height: 8, borderRadius: 4 }} color="warning" />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketplaceDashboard;
